import React, { useState, useCallback, useContext } from 'react'
import { placeholderFunction } from '@talentinc/gatsby-theme-ecom/utils/logging'

interface MultiStepModalData {
  currentStep: number
  open: boolean
}

interface MultiStepModalActions {
  handleOpen: React.MouseEventHandler
  handleClose: () => void
  advanceStep: () => void
}

type IMultiStepModalContext = MultiStepModalData & MultiStepModalActions

export const MultiStepModalContext =
  React.createContext<IMultiStepModalContext>({
    currentStep: 0,
    open: false,
    handleOpen: placeholderFunction,
    handleClose: placeholderFunction,
    advanceStep: placeholderFunction,
  })

interface MultiStepModalProviderProps {
  onOpen?: (() => void) | React.MouseEventHandler
  onClose?: (currentStep: number) => void
  onAdvanceStep?: (currentStep: number) => void
  children: React.ReactNode
}

export const MultiStepModalProvider: React.FC<MultiStepModalProviderProps> = ({
  children,
  onOpen,
  onClose,
  onAdvanceStep,
}) => {
  const [state, setState] = useState<MultiStepModalData>({
    currentStep: 0,
    open: false,
  })

  const handleOpen = useCallback<React.MouseEventHandler>(
    (event) => {
      setState((s) => ({ ...s, open: true }))
      onOpen?.(event)
    },
    [onOpen]
  )

  const handleClose = useCallback(() => {
    setState((s) => {
      onClose?.(s.currentStep)

      return {
        ...s,
        open: false,
      }
    })

    // Reset to step 1 so that the modal doesn't flash back to step 0 while
    // closing.
    setTimeout(() => setState((s) => ({ ...s, currentStep: 0 })), 500)
  }, [onClose])

  const advanceStep = useCallback(() => {
    setState((s) => {
      onAdvanceStep?.(s.currentStep + 1)

      return {
        ...s,
        currentStep: s.currentStep + 1,
      }
    })
  }, [onAdvanceStep])

  return (
    <MultiStepModalContext.Provider
      value={{ ...state, handleOpen, handleClose, advanceStep }}
    >
      {children}
    </MultiStepModalContext.Provider>
  )
}

export default function useMultiStepModal(): IMultiStepModalContext {
  return useContext(MultiStepModalContext)
}
